import { useEffect, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { useAuthUser } from '@/hooks/useAuthUser';
import { resolveTemplateLineage } from '@/modules/templateScenes/templateSubmitService';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import { isTemplateDifficulty } from './templateOptions';
import type { TemplateDifficulty } from './types';
import type { TemplateSubmissionStatus } from '@/types/templateSubmissionTypes';

export interface TemplateMetadata {
  // The copy's own name. Empty until a template exists, so the caller falls back
  // to the story's name.
  name: string;
  description: string;
  category: string;
  difficulty: TemplateDifficulty | '';
  tags: string[];
  learnings: string;
  features: string[];
  submissionStatus: TemplateSubmissionStatus | null;
}

const EMPTY: TemplateMetadata = {
  name: '',
  description: '',
  category: '',
  difficulty: '',
  tags: [],
  learnings: '',
  features: [],
  submissionStatus: null,
};

// Reads what a previous template submission already saved on the origin so the
// template steps open pre-filled, including for stories converted before the
// publish flow owned this metadata.
export function useTemplateMetadata(sceneOriginId: string | null | undefined, enabled: boolean) {
  const services = useServices();
  const { user } = useAuthUser();
  const userId = user?.id;
  const [data, setData] = useState<TemplateMetadata | null>(null);

  // Disabling, or losing the story or the user, clears what was read
  const active = enabled && !!sceneOriginId && !!userId;
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setData(null);
  }

  useEffect(() => {
    if (!enabled || !sceneOriginId || !userId) return;
    let cancelled = false;
    void (async () => {
      const [origin, lineage] = await Promise.all([
        readStory(sceneOriginId),
        resolveTemplateLineage(services, sceneOriginId, userId).catch(() => null),
      ]);
      if (cancelled) return;
      const difficulty = String(origin?.difficulty ?? '');
      const template = lineage?.templateSceneOriginId
        ? await readTemplateOrigin(lineage.templateSceneOriginId)
        : { name: '', description: '' };
      if (cancelled) return;
      setData({
        name: template.name,
        description: template.description,
        category: origin?.category ?? '',
        difficulty: isTemplateDifficulty(difficulty) ? difficulty : '',
        tags: origin?.tags ?? [],
        learnings: origin?.learnings ?? '',
        features: origin?.features ?? [],
        submissionStatus: lineage?.submission?.status ?? null,
      });
    })().catch(() => {
      if (!cancelled) setData(EMPTY);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, sceneOriginId, userId, services]);

  return data;
}

// A story that is missing or unreadable reads as none, as the old maybeSingle read did
async function readStory(storyId: string) {
  const { data } = await toResult(v1.GET('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: storyId } } }));
  return data;
}

// The gallery renders the copy's own name and description, so a resubmission
// pre-fills from there rather than from the story's audience-facing text.
async function readTemplateOrigin(templateSceneOriginId: string): Promise<{ name: string; description: string }> {
  const story = await readStory(templateSceneOriginId);
  return {
    name: story?.name ?? '',
    description: splitDescription(story?.description ?? '').userText,
  };
}
